import React, { Fragment, useMemo, useRef } from 'react';
import { Divider, Grid, IconButton, Radio, Tooltip, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { AssetType, InventoryFileEntity } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectFileTypes } from '@/redux/selectors/constantsSelectors';
import { selectTitle } from '@/redux/selectors/orderPlacementSelectors';
import { setInventoryModalPreviewedFile, setInventoryModalSelectedFile } from '@/redux/slices/packagingSlice';
import { selectInventoryModalPreviewedFile, selectInventoryModalSelectedFile } from '@/redux/selectors/packagingSelectors';
import FileInventoryTags from './FileInventoryTags';
import { DateTimeDisplay } from '@/exports/columnCommonComponents';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { MASTER_VIDEO_LABELS, MASTER_TO_PROXY_MAP } from '@/components/Packaging/constants';
import { useToast } from '@/components/_common/Toast';
import { formatDuration } from '@/components/Packaging/utils';
import { formatBytes } from '@/_utils/utils';
import { useGetFilteredInventory } from '@/components/Packaging/InventoryAssignmentModal/useGetFilteredInventory.hook';

// TODO: `allowedMediaTypes` array only while filters inside InventoryAssignmentModal does not work properly.
//  Once filters are implemented properly, there should not be files in the list of not suppoerted media types.
const allowedMediaTypes: AssetType[] = ['video', 'image'];

type MasterLabel = (typeof MASTER_VIDEO_LABELS)[number];

const useMasterIdToProxyMap = (inventory: InventoryFileEntity[]) => {
  return useMemo(() => {
    const masterFiles = inventory.filter((file) => MASTER_VIDEO_LABELS.includes(file.label as MasterLabel));
    const masterToProxyMap = new Map<InventoryFileEntity['id'], InventoryFileEntity | undefined>();
    return masterFiles.reduce((acc, masterFile) => {
      const proxyFile = inventory.find((file) => file.label === MASTER_TO_PROXY_MAP[masterFile.label as MasterLabel]);
      acc.set(masterFile.id, proxyFile);
      return acc;
    }, masterToProxyMap);
  }, [inventory]);
};

const MISSING_VALUE_INDICATOR = '—';

const PackagingInventoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const fileTypes = useAppSelector(selectFileTypes);
  const inventory: InventoryFileEntity[] = useAppSelector(selectTitle);
  const selectedFile = useAppSelector(selectInventoryModalSelectedFile);
  const previewedFile = useAppSelector(selectInventoryModalPreviewedFile);
  const nameRef = useRef<HTMLDivElement>(null);
  const { error } = useToast();

  const filteredInventory = useGetFilteredInventory();

  const masterToProxyMap = useMasterIdToProxyMap(inventory);

  const colGrid = {
    radio: 0.5,
    name: 6,
    size: 1.25,
    duration: 1.25,
    date_added: 2,
    preview: 0.5
  };

  const handlerPreviewButtonClick = (inventoryFile: InventoryFileEntity) => {
    const mediaType = fileTypes[inventoryFile.file_type];
    const isVideo = mediaType && mediaType === 'video';
    const proxyFile = isVideo ? masterToProxyMap.get(inventoryFile.id) : undefined;

    // if selected file is a video file and it doesn't have corresponding proxy file
    if (isVideo && !proxyFile) {
      error('This file does not have corresponding proxy file, so it can not be reviewed');
      dispatch(setInventoryModalPreviewedFile(null));
      return;
    }

    if (previewedFile && inventoryFile.id === previewedFile.id) {
      dispatch(setInventoryModalPreviewedFile(null));
      return;
    }

    dispatch(
      setInventoryModalPreviewedFile({
        ...inventoryFile,
        proxyId: proxyFile?.id
      })
    );
  };

  const handleSelectionChange = (inventoryFile: InventoryFileEntity) => {
    const mediaType = fileTypes[inventoryFile.file_type];
    const isVideo = mediaType && mediaType === 'video';
    const proxyFile = isVideo ? masterToProxyMap.get(inventoryFile.id) : inventoryFile;
    dispatch(
      setInventoryModalSelectedFile({
        ...inventoryFile,
        proxyId: proxyFile?.id
      })
    );
  };

  return (
    <Wrapper>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ opacity: 0.4, pt: 1 }}>
        <Grid item xs={colGrid.radio} />
        <Grid item xs={colGrid.name} ref={nameRef}>
          Name
        </Grid>
        <Grid item xs={colGrid.size}>
          Size
        </Grid>
        <Grid item xs={colGrid.duration}>
          Duration
        </Grid>

        <Grid item xs={colGrid.date_added}>
          Date Added
        </Grid>
        <Grid item xs={colGrid.preview} />
      </Grid>
      <Divider sx={{ margin: `15px 0px` }} />
      <LineItemWrapper>
        {filteredInventory.length > 0 ? (
          filteredInventory.map((inventoryFile, index) => {
            const mediaType = fileTypes[inventoryFile.file_type];
            const previewEnabled = mediaType ? allowedMediaTypes.includes(mediaType) : false;
            const videoDuration: string | undefined = inventoryFile.media_info?.parsed_video?.duration;

            return (
              <Fragment key={inventoryFile.id}>
                <FileLineItem container justifyContent="space-between" alignItems="center">
                  <Grid item xs={colGrid.radio} sx={{ minWidth: 'fit-content' }}>
                    <Radio
                      name="inventory"
                      checked={!!(selectedFile && inventoryFile.id === selectedFile.id)}
                      value={inventoryFile.id}
                      onChange={() => handleSelectionChange(inventoryFile)}
                    />
                  </Grid>
                  <NameColumn item xs={colGrid.name}>
                    <Tooltip title={inventoryFile.name} placement="top" arrow>
                      <Typography variant="subtitle2" sx={{ fontSize: '1.2rem', textOverflow: 'ellipsis' }} noWrap>
                        {inventoryFile.name}
                      </Typography>
                    </Tooltip>
                    <StyledTags file={inventoryFile} />
                  </NameColumn>
                  <Grid item xs={colGrid.size}>
                    {formatBytes(inventoryFile.file_size)}
                  </Grid>
                  <Grid item xs={colGrid.duration}>
                    {videoDuration ? (
                      formatDuration(inventoryFile.media_info?.parsed_video?.duration)
                    ) : (
                      <DurationMissingValue>{MISSING_VALUE_INDICATOR}</DurationMissingValue>
                    )}
                  </Grid>

                  <Grid item xs={colGrid.date_added}>
                    <DateTimeDisplay missingText={MISSING_VALUE_INDICATOR}>{inventoryFile.date_added}</DateTimeDisplay>
                  </Grid>
                  <Grid item xs={colGrid.preview}>
                    <Tooltip title={previewEnabled ? 'Preview' : 'Preview not available for this media type'}>
                      <span>
                        <PreviewButton
                          disabled={!previewEnabled}
                          isPreviewed={previewedFile ? previewedFile.id === inventoryFile.id : false}
                          onClick={() => handlerPreviewButtonClick(inventoryFile)}
                        >
                          <PolarisIcon height={15} icon={previewEnabled ? 'preview' : 'hidden'} />
                        </PreviewButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </FileLineItem>
                {index < filteredInventory.length - 1 && <Divider sx={{ margin: `15px 0px` }} />}
              </Fragment>
            );
          })
        ) : (
          <NoFilesMessage>No files found</NoFilesMessage>
        )}
      </LineItemWrapper>
    </Wrapper>
  );
};

export default PackagingInventoryList;

const Wrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  height: 100%;
  position: relative;
`;

const FileLineItem = styled(Grid)`
  height: 50px;
`;

const NameColumn = styled(Grid)`
  font-size: 1.2rem;
`;

const StyledTags = styled(FileInventoryTags)`
  .MuiChip-root {
    height: 19px;

    > span > div > span {
      font-size: 0.8rem;
    }
  }
`;

const LineItemWrapper = styled.div`
  height: 100%;
  position: relative;
  min-height: 100px;
`;

const PreviewButton = styled(IconButton, {
  shouldForwardProp: (prop) => !['isPreviewed'].includes(prop)
})<{ isPreviewed: boolean }>`
  ${({ theme, isPreviewed }) => (isPreviewed ? `color: ${theme.palette.primary.main};` : undefined)};
`;

const NoFilesMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.2rem;
  font-style: italic;
  opacity: 0.5;
`;

const DurationMissingValue = styled.div`
  font-size: 0.9rem;
`;
